import { useState, useRef } from "react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { UploadCloud, FileSpreadsheet, Loader2, Link2, Download } from "lucide-react";
import { api, riderIdsTemplateUrl } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";

export const RiderIdImportDialog = ({ open, onOpenChange, onImported }) => {
  const { dir } = useLang();
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef();

  const reset = () => { setFile(null); setResult(null); };

  const handleImport = async () => {
    if (!file) { toast.error("اختر ملفاً أولاً"); return; }
    setBusy(true);
    try {
      const res = await api.importRiderIds(file);
      setResult(res);
      toast.success(`تم ربط ${res.updated} معرّف مندوب`);
      onImported && onImported();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّر استيراد المعرفات");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="sm:max-w-md font-body" dir={dir}>
        <DialogHeader className="text-start">
          <DialogTitle className="font-heading text-xl">استيراد معرفات المندوبين</DialogTitle>
          <DialogDescription className="text-start">
            ارفع ملف Excel يحتوي على عمودين: «رقم المندوب (Rider ID)» و«الهوية». سيتم ربط كل معرّف مندوب تلقائياً بالموظف المطابق برقم الهوية.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <a href={riderIdsTemplateUrl} data-testid="download-rider-template-link"
            className="inline-flex items-center gap-2 text-sm font-medium text-blue-700 hover:underline">
            <Download className="h-4 w-4" /> تنزيل قالب Excel جاهز
          </a>

          <label data-testid="rider-import-dropzone"
            className="flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-slate-300 p-6 cursor-pointer hover:bg-slate-50 transition">
            <UploadCloud className="h-8 w-8 text-slate-400" />
            {file ? (
              <span className="flex items-center gap-2 text-sm text-slate-700"><FileSpreadsheet className="h-4 w-4" /> {file.name}</span>
            ) : (
              <span className="text-sm text-slate-500">اختر ملف Excel أو CSV</span>
            )}
            <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls" className="hidden"
              data-testid="rider-import-file-input"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }} />
          </label>

          {result && (
            <div data-testid="rider-import-result" className="rounded-xl bg-blue-50 border border-blue-200 p-3 text-sm text-blue-800 space-y-1">
              <div>تم ربط <b>{result.updated}</b> مندوب بنجاح.</div>
              {result.not_found > 0 && (
                <div className="text-amber-700">لم يُعثر على موظف مطابق لـ <b>{result.not_found}</b> هوية.</div>
              )}
              {result.skipped > 0 && (
                <div className="text-slate-500">تم تخطّي {result.skipped} صف (بيانات ناقصة).</div>
              )}
              {result.not_found_ids?.length > 0 && (
                <div className="mt-2 pt-2 border-t border-blue-200">
                  <div className="text-amber-800 font-medium mb-1">الهويات غير الموجودة (صحّحها ثم أعد الرفع):</div>
                  <div data-testid="rider-import-not-found" className="max-h-32 overflow-auto flex flex-wrap gap-1.5" dir="ltr">
                    {result.not_found_ids.map((iq) => (
                      <span key={iq} className="inline-block rounded-md bg-white border border-amber-300 px-2 py-0.5 text-xs text-amber-800 font-mono">{iq}</span>
                    ))}
                  </div>
                  {result.not_found > result.not_found_ids.length && (
                    <div className="text-xs text-slate-500 mt-1">…و{result.not_found - result.not_found_ids.length} هوية أخرى.</div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex-row-reverse gap-2 sm:justify-start">
          <Button data-testid="confirm-rider-import-button" disabled={busy || !file}
            className="bg-blue-600 hover:bg-blue-700 active:scale-95 transition gap-2" onClick={handleImport}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
            {busy ? "جارٍ الاستيراد…" : "استيراد وربط"}
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>إغلاق</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
